import { cn } from "@/lib/utils";
import type {
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripDotsVertical } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import {
  createContext,
  useContext,
  useMemo,
  type CSSProperties,
  type PropsWithChildren,
} from "react";

type SortableItemProps<C extends React.ElementType> = {
  id: UniqueIdentifier;
  as: C;
  className?: string;
} & React.ComponentPropsWithoutRef<C>;

interface Context {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
}

const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {},
});

export const SortableItem = <C extends React.ElementType>({
  children,
  id,
  as,
  className,
  ...rest
}: PropsWithChildren<SortableItemProps<C>>) => {
  const {
    attributes,
    isSorting,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef],
  );
  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  // I could not figure out how to strong type components with refs 😭
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const BaseComponent = as as any;

  return (
    <SortableItemContext.Provider value={context}>
      <BaseComponent
        {...rest}
        ref={setNodeRef}
        style={style}
        className={cn(className, {
          "opacity-100": isDragging,
          "opacity-50": isSorting && !isDragging,
        })}
      >
        {children}
      </BaseComponent>
    </SortableItemContext.Provider>
  );
};

export function SortableItemHandle({ className }: { className?: string }) {
  const { attributes, listeners, ref } = useContext(SortableItemContext);

  return (
    <span
      {...attributes}
      {...listeners}
      ref={ref}
      className={cn("cursor-grab", className)}
    >
      <FontAwesomeIcon icon={faGripDotsVertical} />
    </span>
  );
}
